/**
 * @file routes/_app/analytics/clients.tsx
 * @description 客戶分析頁。
 *
 * 顯示客戶驗證狀態分布（PieChart）和繳費狀態分布（BarChart）。
 *
 * 視覺：PageShell + StatCard + SectionCard + EmptyState（recharts PieChart + BarChart）
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Button, Space, Spin } from 'antd';
import { message } from '@/lib/antd-app';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FileDown, RefreshCw, Users, UserCheck } from 'lucide-react';
import { useClientStats } from '@/hooks/useAnalytics';
import { PageShell, SectionCard, StatCard, EmptyState } from '@/components/shell';
import { space } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/analytics/clients')({
  component: AnalyticsClientsPage,
});

const VERIFICATION_COLORS = [
  '#4f46e5',
  '#059669',
  '#d97706',
  '#e11d48',
  '#0891b2',
  '#7c3aed',
  '#db2777',
];
const PAYMENT_COLORS = ['#059669', '#d97706', '#e11d48'];

/** 客戶分析頁 */
function AnalyticsClientsPage() {
  const [exporting, setExporting] = useState(false);
  const { data, isLoading, isFetching, refetch } = useClientStats();

  const verDist = data?.verificationStatusDist ?? [];
  const payDist = data?.paymentStatusDist ?? [];

  /* ── 匯出（此頁無後端單獨匯出端點，使用客戶 overview 匯出） ── */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await fetch('/api/analytics/export?type=overview', {
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zhcb-clients-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('匯出成功');
    } catch (err) {
      message.error('匯出失敗');
      console.error('export clients analytics failed', err);
    } finally {
      setExporting(false);
    }
  }, []);

  return (
    <PageShell
      title="客戶分析"
      description="客戶驗證狀態分布與繳費健康度"
      actions={
        <Space>
          <Button
            icon={<RefreshCw size={14} />}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            刷新
          </Button>
          <Button icon={<FileDown size={14} />} loading={exporting} onClick={handleExport}>
            匯出 Excel
          </Button>
        </Space>
      }
    >
      {/* KPI */}
      {!isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))',
            gap: space[4],
            marginBottom: space[4],
          }}
        >
          <StatCard
            title="總客戶數"
            value={data?.totalClients ?? 0}
            suffix="家"
            tone="neutral"
            icon={<Users size={18} />}
          />
          <StatCard
            title="本月新增"
            value={data?.newClientsThisMonth ?? 0}
            suffix="家"
            tone="brand"
            icon={<UserCheck size={18} />}
          />
        </div>
      )}

      {/* 圖表區 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space[4] }}>
        {/* 驗證狀態 PieChart */}
        <SectionCard title="客戶驗證狀態分布">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : verDist.length === 0 ? (
            <EmptyState title="暫無客戶資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={verDist}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="45%"
                  outerRadius={90}
                  label={(props) => {
                    const p = props as unknown as { status?: string; count?: number };
                    return `${p.status ?? ''}(${p.count ?? ''})`;
                  }}
                  labelLine={false}
                >
                  {verDist.map((_, idx) => (
                    <Cell key={idx} fill={VERIFICATION_COLORS[idx % VERIFICATION_COLORS.length]} />
                  ))}
                </Pie>
                <RechartTooltip formatter={(v, n) => [v, n]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        {/* 繳費狀態 BarChart */}
        <SectionCard title="客戶繳費狀態分布">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : payDist.length === 0 ? (
            <EmptyState title="暫無繳費資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={payDist} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <RechartTooltip />
                <Bar dataKey="count" name="客戶數" radius={[3, 3, 0, 0]}>
                  {payDist.map((_, idx) => (
                    <Cell key={idx} fill={PAYMENT_COLORS[idx % PAYMENT_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
